import { yupResolver } from "@hookform/resolvers/yup";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import TitledBox from "../../../../../components/TitledBox";
import ControlledTextField from "../../../../../components/controlled/ControlledTextField";

import { useAddRecipientsToGroupMutation } from "../../../recipientsApiSlice";

import { getFormSchema } from "./formSchema";

import { useTranslation } from "react-i18next";
import removeIcon from "../../../../../assets/icons/delete.png";
import ControlledPhoneInput from "../../../../../components/controlled/ControlledPhoneInput";

const MAX = 300;

const AddDialog = ({ onClose, open, groupId, count }) => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [addRecipients, { isLoading }] = useAddRecipientsToGroupMutation();
	const [rowErrors, setRowErrors] = useState([]);

	const formSchema = getFormSchema(t);
	const {
		handleSubmit,
		control,
		formState: { errors, isSubmitSuccessful },
		setError,
		getValues,
		reset,
	} = useForm({
		resolver: yupResolver(formSchema),
		shouldFocusError: true,
	});

	const {
		fields: recipients,
		append: appendRecipientRow,
		remove: removeRecipientRow,
	} = useFieldArray({
		control,
		name: "recipients",
	});

	const handleAppendRecipient = ({
		name,
		firstName,
		lastName,
		email,
		phone,
		nip,
	}) => {
		if (count + recipients.length >= MAX) {
			enqueueSnackbar(t("adding_recipient_will_exceed_max"), {
				variant: "error",
			});

			return;
		}

		appendRecipientRow({
			name,
			firstName,
			lastName,
			email,
			phone,
			...(nip && { nip }),
		});
	};

	const submitNewRecipients = async () => {
		const formValues = getValues();
		const recipients = formValues.recipients;

		setRowErrors([]);

		await addRecipients({ groupId, data: recipients })
			.unwrap()
			.then((_payload) => {
				enqueueSnackbar(t("added_carrier_to_mailing_group"), {
					variant: "success",
				});
				onClose();
			})
			.catch((error) => {
				if (error?.data?.errors) {
					const errors = error.data.errors;
					Object.entries(errors).forEach(([key, message]) => {
						const splittedKey = key.split(".");
						const parsedMessage = message[0].replace(key, splittedKey[2]);
						const errorIndex = Number(splittedKey[1]);
						setRowErrors((prevRowErrors) => [...prevRowErrors, errorIndex]);
						enqueueSnackbar(parsedMessage, {
							variant: "error",
							preventDuplicate: true,
						});
					});
				} else {
					enqueueSnackbar(error?.data?.message || t("default_error"), {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
	};

	useEffect(() => {
		if (open) {
			reset({ recipients: [] });
		}
	}, [open, reset]);

	useEffect(() => {
		if (isSubmitSuccessful) {
			reset({ recipients });
		}
	}, [isSubmitSuccessful, reset, recipients]);

	useEffect(() => {
		if (Object.keys(errors).length > 0) {
			Object.values(errors).forEach((err) => {
				enqueueSnackbar(err.message, {
					variant: "error",
					preventDuplicate: true,
				});
			});
		}
	}, [errors, enqueueSnackbar]);

	const handleRemoveRow = (index) => {
		removeRecipientRow(index);
		setRowErrors((prevRowErrors) => {
			return prevRowErrors
				.filter((err) => err !== index)
				.map((err) => (err > index ? err - 1 : err));
		});
	};

	return (
		<Dialog fullWidth maxWidth="md" onClose={onClose} open={open}>
			<Box sx={{ padding: "30px" }}>
				<Box
					component="form"
					noValidate
					onSubmit={handleSubmit(handleAppendRecipient)}
				>
					<Grid container rowSpacing={3.6} columnSpacing={2}>
						<Grid
							size={{
								lg: 12,
							}}
						>
							<TitledBox
								title={t("carrier_data")}
								color="#0069b4"
								sx={{ width: "100%", height: "100%" }}
							>
								<Grid
									container
									rowSpacing={0}
									columnSpacing={1}
									sx={{ py: 1, px: 2 }}
								>
									<Grid
										size={{
											lg: 12,
										}}
									>
										<ControlledTextField
											margin="normal"
											label={t("name_label")}
											control={control}
											name="name"
											required
											error={!!errors?.name}
											defaultValue=""
											fullWidth
										/>
									</Grid>
									<Grid
										size={{
											lg: 6,
										}}
									>
										<ControlledTextField
											margin="normal"
											label={t("first_name_label")}
											control={control}
											name="firstName"
											error={!!errors?.firstName}
											defaultValue=""
											fullWidth
										/>
									</Grid>
									<Grid
										size={{
											lg: 6,
										}}
									>
										<ControlledTextField
											margin="normal"
											label={t("surname_label")}
											control={control}
											name="lastName"
											error={!!errors?.lastName}
											defaultValue=""
											fullWidth
										/>
									</Grid>
								</Grid>
							</TitledBox>
						</Grid>
						<Grid
							size={{
								lg: 6,
							}}
						>
							<TitledBox
								title={t("contact")}
								color="#63c29a"
								sx={{ width: "100%", height: "100%" }}
							>
								<Grid
									container
									rowSpacing={0}
									columnSpacing={1}
									sx={{ py: 1, px: 2 }}
								>
									<Grid
										size={{
											lg: 12,
										}}
									>
										<ControlledTextField
											control={control}
											label={t("email_label")}
											margin="normal"
											name="email"
											type="email"
											required
											defaultValue=""
											error={!!errors?.email}
											fullWidth
										/>
									</Grid>
									<Grid
										size={{
											lg: 12,
										}}
									>
										<ControlledPhoneInput
											control={control}
											label={t("phone_label")}
											margin="normal"
											name="phone"
											type="tel"
											defaultValue=""
											error={!!errors?.phone}
											fullWidth
										/>
									</Grid>
								</Grid>
							</TitledBox>
						</Grid>
						<Grid
							size={{
								lg: 6,
							}}
						>
							<TitledBox
								title={t("nip_number_label")}
								color="#7f7f7f"
								sx={{ width: "100%", height: "100%" }}
							>
								<Grid
									container
									rowSpacing={0}
									columnSpacing={1}
									sx={{ py: 1, px: 2 }}
								>
									<Grid
										size={{
											lg: 12,
										}}
									>
										<ControlledTextField
											margin="normal"
											control={control}
											name="nip"
											error={!!errors?.nip}
											fullWidth
											defaultValue=""
											sx={{ mb: 0 }}
										/>
									</Grid>
									<Grid size={12}>
										<Typography
											component="span"
											sx={{ color: "#A6A6A6", fontSize: "11px" }}
										>
											{t("nip_number_info")}
										</Typography>
									</Grid>
								</Grid>
							</TitledBox>
						</Grid>
						<Grid size={6}>
							<Box sx={{ display: "flex", justifyContent: "end" }}>
								<Button type="submit" variant="contained" color="secondary">
									{t("add")}
								</Button>
							</Box>
						</Grid>
						<Grid size={6}>
							<Box sx={{ display: "flex", justifyContent: "start" }}>
								<Button
									loading={isLoading}
									onClick={submitNewRecipients}
									variant="contained"
									disabled={recipients.length < 1}
								>
									{t("save")}
								</Button>
							</Box>
						</Grid>
						{recipients.length > 0 && (
							<Grid
								container
								rowSpacing={0}
								columnSpacing={2}
								sx={{ mt: "-20px" }}
								size={{
									lg: 12,
								}}
							>
								<Grid size={12}>
									<Box sx={{ borderBottom: "1px solid #7F7F7F", mb: 1 }}>
										<Typography
											sx={{
												background: "#7F7F7F",
												color: "#fff",
												width: "160px",
												pl: "10px",
											}}
										>
											{t("to_save")}
										</Typography>
									</Box>
								</Grid>
								<Grid size={12}>
									<Box
										component="table"
										sx={{
											width: "100%",
											borderCollapse: "separate",
											borderSpacing: "0 3px",
										}}
									>
										{recipients.map((field, index) => (
											<Box
												key={field.id}
												component="tr"
												sx={{
													width: "100%",
													background: rowErrors.includes(index)
														? "#d32f2f1d"
														: "#F5F5F5",
												}}
											>
												<td>{field?.name}</td>
												<td>{field?.firstName}</td>
												<td>{field?.lastName}</td>
												<td>{field?.email}</td>
												<td>{field?.phone?.length > 3 ? field?.phone : ""}</td>
												<td>{field?.nip}</td>
												<td style={{ width: "1%" }}>
													<Button
														color="error"
														variant="text"
														onClick={() => handleRemoveRow(index)}
														sx={{
															background: "transparent",
															"&:hover": {
																background: "transparent",
															},
														}}
													>
														<Box
															sx={{
																display: "flex",
																alignItems: "center",
																justifyContent: "center",
																cursor: "pointer",
																height: "16px",
																width: "16px",
															}}
														>
															<img
																src={removeIcon}
																alt=""
																style={{
																	width: "100%",
																	height: "100%",
																	objectFit: "contain",
																}}
															/>
														</Box>
													</Button>
												</td>
											</Box>
										))}
									</Box>
								</Grid>
							</Grid>
						)}
					</Grid>
				</Box>
			</Box>
		</Dialog>
	);
};

export default AddDialog;
